import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import { translateKey } from 'src/locales/translate';
import WarehouseProductBadges from 'src/sections/@dashboard/warehouse/components/products/WarehouseProductBadges';
import { useWarehouseProduct } from 'src/sections/@dashboard/warehouse/hooks/useWarehouseProduct';
import {
  formatProductStock,
  todayDateOnly,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import MedicineStockBatchList from './MedicineStockBatchList';
import { summaryBatchRows } from './medicineStockBatchRows';

const s = (key: string, options?: Record<string, unknown>): string =>
  translateKey(`warehouse.medicineStock.${key}`, options);

interface MedicineStockSummaryProps {
  /** The Warehouse Product the form's drug is matched to; '' when none. */
  productId: string;
  /** A registry medicine picked while the search listed no Product for it (R13). */
  missingMedicineName?: string;
  /** Opens "Zgłoś zapotrzebowanie" for that name; without it no button. */
  onRequestDemand?: (name: string) => void;
}

/** One Product's stock, read through the Magazyn page's own detail query. */
function ProductStock({
  productId,
}: {
  productId: string;
}): React.ReactElement {
  const { data: product, isError } = useWarehouseProduct(productId);
  if (isError) {
    return (
      <Typography
        variant="caption"
        color="error"
        data-testid="medicine-stock-error"
      >
        {s('loadError')}
      </Typography>
    );
  }
  if (!product) {
    return <Skeleton variant="text" data-testid="medicine-stock-loading" />;
  }
  return (
    <Stack spacing={0.75} data-testid="medicine-stock-summary">
      <Typography variant="subtitle2">{s('title')}</Typography>
      <Typography
        variant="caption"
        color={product.onHandMilli <= 0 ? 'error' : 'text.primary'}
      >
        {s('stock', {
          quantity: formatProductStock(product.onHandMilli, product),
        })}
      </Typography>
      {product.minStockMilli !== undefined &&
        product.minStockMilli !== null && (
          <Typography variant="caption">
            {s('minimum', {
              quantity: formatProductStock(product.minStockMilli, product),
            })}
          </Typography>
        )}
      <WarehouseProductBadges product={product} />
      <MedicineStockBatchList
        rows={summaryBatchRows(product.batches, todayDateOnly())}
        product={product}
      />
    </Stack>
  );
}

/**
 * "W naszym magazynie" under the manufacturer's data (#690): what the
 * Facility holds of the Product the form's drug is matched to, or that the
 * search found none for the picked registry medicine. A failed read says so
 * in one line and never blocks the form.
 */
export default function MedicineStockSummary({
  productId,
  missingMedicineName,
  onRequestDemand,
}: MedicineStockSummaryProps): React.ReactElement | null {
  if (productId) {
    return <ProductStock productId={productId} />;
  }
  if (!missingMedicineName) {
    return null;
  }
  return (
    <Stack spacing={1} data-testid="medicine-stock-missing">
      <Typography variant="caption">{s('notInWarehouse')}</Typography>
      {onRequestDemand && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            data-testid="medicine-stock-request-demand"
            onClick={() => onRequestDemand(missingMedicineName)}
          >
            {s('requestDemand')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
